import apiClient from './api';
import { mockAssessmentData } from './mock/assessments.mock';
import { API_ENDPOINTS } from '@/config/api.config';
import {
  AssessmentAdmin,
  AssessmentLearner,
  ApiResponse,
  CourseWithAssessments,
  ModuleContentTreeResponse,
  AttemptStartResponse,
  SubmitAttemptRequest,
  AttemptAnswer,
  AttemptSubmitResponse,
  AttemptHistoryItem,
  UserAssessmentSummary,
} from '@/types/assessment.types';

// Interfaces for type safety
interface Filters {
  course?: string;
  status?: string;
  search?: string;
  page?: number;
  pageSize?: number;
  category?: string;
  type?: string;
  difficulty?: string;
}

interface QuizData {
  [key: string]: any;
}

interface AssignmentData {
  [key: string]: any;
}

interface QuestionData {
  [key: string]: any;
}

class AssessmentService {
  private useMockData: boolean;

  constructor() {
    this.useMockData = true; // Switch to false when real API is ready
  }

  // ============================================================================
  // NEW ASSESSMENT API METHODS (v6.0.210)
  // ============================================================================

  /**
   * Get course details with assessment summaries (pre/post at course level)
   * GET /course/:courseId
   */
  async getCourseAssessmentSummary(courseId: string): Promise<CourseWithAssessments> {
    try {
      const response = await apiClient.get<ApiResponse<CourseWithAssessments>>(
        API_ENDPOINTS.COURSES.GET_BY_ID(courseId)
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching course assessment summary:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch course assessment summary'
      );
    }
  }

  /**
   * Get module content tree with all assessment summaries
   * Includes module-level and content-item-level assessments (pre/post)
   * GET /course/module/:moduleId/content-tree
   */
  async getModuleAssessmentSummary(moduleId: string): Promise<ModuleContentTreeResponse> {
    try {
      const response = await apiClient.get<ApiResponse<ModuleContentTreeResponse>>(
        API_ENDPOINTS.COURSES.GET_MODULE_CONTENT_TREE(moduleId)
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching module assessment summary:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch module assessment summary'
      );
    }
  }

  /**
   * Get full assessment details with correct answers (ADMIN VIEW ONLY)
   * Includes all questions with correct answers, explanations, and hints
   * GET /course/assessment/:assessmentId/admin
   */
  async getAssessmentAdmin(assessmentId: string): Promise<AssessmentAdmin> {
    try {
      const response = await apiClient.get<ApiResponse<AssessmentAdmin>>(
        API_ENDPOINTS.ASSESSMENTS.GET_ADMIN(assessmentId)
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching admin assessment:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch admin assessment'
      );
    }
  }

  /**
   * Get sanitized assessment for learners (NO CORRECT ANSWERS)
   * Includes only questions and options, no answers/explanations
   * GET /course/assessment/:assessmentId/learner
   */
  async getAssessmentLearner(assessmentId: string, languageCode?: string): Promise<AssessmentLearner> {
    try {
      const response = await apiClient.get<ApiResponse<AssessmentLearner>>(
        API_ENDPOINTS.ASSESSMENTS.GET_LEARNER(assessmentId, languageCode)
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching learner assessment:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch learner assessment'
      );
    }
  }

  // ============================================================================
  // PART 2: ASSESSMENT SUBMISSION & SCORING METHODS (v6.0.211)
  // ============================================================================

  /**
   * Start a new assessment attempt
   * POST /course/assessment/:assessmentId/start
   */
  async startAttempt(assessmentId: string, languageCode?: string): Promise<AttemptStartResponse> {
    try {
      const response = await apiClient.post<ApiResponse<AttemptStartResponse>>(
        API_ENDPOINTS.ASSESSMENTS.START_ATTEMPT(assessmentId, languageCode)
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Error starting assessment attempt:', error);
      throw error; // Throw original error to preserve response structure
    }
  }

  /**
   * Submit assessment attempt for grading
   * POST /course/assessment/attempt/:attemptId/submit
   */
  async submitAttempt(attemptId: string, answers: AttemptAnswer[]): Promise<AttemptSubmitResponse> {
    try {
      const requestBody: SubmitAttemptRequest = {
        attempt_id: attemptId,
        answers,
      };
      const response = await apiClient.post<ApiResponse<AttemptSubmitResponse>>(
        API_ENDPOINTS.ASSESSMENTS.SUBMIT_ATTEMPT(attemptId),
        requestBody
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Error submitting assessment attempt:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to submit assessment attempt'
      );
    }
  }

  /**
   * Get attempt history for an assessment
   * GET /course/assessment/:assessmentId/attempts
   */
  async getAttemptHistory(assessmentId: string): Promise<AttemptHistoryItem[]> {
    try {
      const response = await apiClient.get<ApiResponse<AttemptHistoryItem[]>>(
        API_ENDPOINTS.ASSESSMENTS.GET_ATTEMPT_HISTORY(assessmentId)
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching attempt history:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch attempt history'
      );
    }
  }

  /**
   * Get detailed information about a specific attempt
   * GET /course/assessment/attempt/:attemptId
   */
  async getAttemptDetails(attemptId: string): Promise<AttemptSubmitResponse> {
    try {
      const response = await apiClient.get<ApiResponse<AttemptSubmitResponse>>(
        API_ENDPOINTS.ASSESSMENTS.GET_ATTEMPT_DETAILS(attemptId)
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching attempt details:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch attempt details'
      );
    }
  }

  /**
   * Get user's assessment summary (statistics and retry info)
   * GET /course/assessment/:assessmentId/summary
   */
  async getUserAssessmentSummary(assessmentId: string): Promise<UserAssessmentSummary> {
    try {
      const response = await apiClient.get<ApiResponse<UserAssessmentSummary>>(
        API_ENDPOINTS.ASSESSMENTS.GET_USER_SUMMARY(assessmentId)
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching assessment summary:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch assessment summary'
      );
    }
  }

  // ============================================================================
  // EXISTING MOCK-BASED METHODS (Keep for backward compatibility)
  // ============================================================================

  // Quiz Management
  async getQuizzes(filters: Filters = {}): Promise<any> {
    if (this.useMockData) {
      return new Promise(resolve => {
        setTimeout(() => {
          let quizzes = [...mockAssessmentData.quizzes];

          if (filters.course) {
            quizzes = quizzes.filter(q => q.courseId === filters.course);
          }
          if (filters.status) {
            quizzes = quizzes.filter(q => q.status === filters.status);
          }
          if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            quizzes = quizzes.filter(
              q =>
                q.title.toLowerCase().includes(searchLower) ||
                q.description.toLowerCase().includes(searchLower)
            );
          }

          resolve({
            data: quizzes,
            total: quizzes.length,
            page: filters.page || 1,
            pageSize: filters.pageSize || 10,
          });
        }, 500);
      });
    }
    // Real API call would go here
    return Promise.resolve({ data: [], total: 0, page: 1, pageSize: 10 });
  }

  async getQuizById(id: string): Promise<any> {
    if (this.useMockData) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          const quiz = mockAssessmentData.quizzes.find(q => q.id === id);
          if (quiz) {
            resolve(quiz);
          } else {
            reject(new Error('Quiz not found'));
          }
        }, 500);
      });
    }
    // Real API call would go here
    return Promise.reject(new Error('Not implemented'));
  }

  async createQuiz(quizData: QuizData): Promise<any> {
    if (this.useMockData) {
      return new Promise(resolve => {
        setTimeout(() => {
          const newQuiz = {
            id: Date.now().toString(),
            ...quizData,
            status: 'draft',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            totalQuestions: quizData.questions?.length || 0,
            attempts: 0,
            averageScore: 0,
          };
          mockAssessmentData.quizzes.push(newQuiz);
          resolve(newQuiz);
        }, 1000);
      });
    }
    // Real API call would go here
    return Promise.reject(new Error('Not implemented'));
  }

  async updateQuiz(id: string, quizData: QuizData): Promise<any> {
    if (this.useMockData) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          const index = mockAssessmentData.quizzes.findIndex(q => q.id === id);
          if (index !== -1) {
            mockAssessmentData.quizzes[index] = {
              ...mockAssessmentData.quizzes[index],
              ...quizData,
              updatedAt: new Date().toISOString(),
              totalQuestions:
                quizData.questions?.length ||
                mockAssessmentData.quizzes[index].totalQuestions,
            };
            resolve(mockAssessmentData.quizzes[index]);
          } else {
            reject(new Error('Quiz not found'));
          }
        }, 1000);
      });
    }
    // Real API call would go here
    return Promise.reject(new Error('Not implemented'));
  }

  async deleteQuiz(id: string): Promise<{ message: string }> {
    if (this.useMockData) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          const index = mockAssessmentData.quizzes.findIndex(q => q.id === id);
          if (index !== -1) {
            mockAssessmentData.quizzes.splice(index, 1);
            resolve({ message: 'Quiz deleted successfully' });
          } else {
            reject(new Error('Quiz not found'));
          }
        }, 500);
      });
    }
    // Real API call would go here
    return Promise.reject(new Error('Not implemented'));
  }

  async duplicateQuiz(id: string): Promise<any> {
    if (this.useMockData) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          const quiz = mockAssessmentData.quizzes.find(q => q.id === id);
          if (quiz) {
            const duplicatedQuiz = {
              ...quiz,
              id: Date.now().toString(),
              title: `${quiz.title} (Copy)`,
              status: 'draft',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              attempts: 0,
              averageScore: 0,
            };
            mockAssessmentData.quizzes.push(duplicatedQuiz);
            resolve(duplicatedQuiz);
          } else {
            reject(new Error('Quiz not found'));
          }
        }, 1000);
      });
    }
    // Real API call would go here
    return Promise.reject(new Error('Not implemented'));
  }

  // Assignment Management
  async getAssignments(filters: Filters = {}): Promise<any> {
    if (this.useMockData) {
      return new Promise(resolve => {
        setTimeout(() => {
          let assignments = [...mockAssessmentData.assignments];

          if (filters.course) {
            assignments = assignments.filter(a => a.courseId === filters.course);
          }
          if (filters.status) {
            assignments = assignments.filter(a => a.status === filters.status);
          }
          if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            assignments = assignments.filter(
              a =>
                a.title.toLowerCase().includes(searchLower) ||
                a.description.toLowerCase().includes(searchLower)
            );
          }

          resolve({
            data: assignments,
            total: assignments.length,
            page: filters.page || 1,
            pageSize: filters.pageSize || 10,
          });
        }, 500);
      });
    }
    // Real API call would go here
    return Promise.resolve({ data: [], total: 0, page: 1, pageSize: 10 });
  }

  async getAssignmentById(id: string): Promise<any> {
    if (this.useMockData) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          const assignment = mockAssessmentData.assignments.find(a => a.id === id);
          if (assignment) {
            resolve(assignment);
          } else {
            reject(new Error('Assignment not found'));
          }
        }, 500);
      });
    }
    // Real API call would go here
    return Promise.reject(new Error('Not implemented'));
  }

  async createAssignment(assignmentData: AssignmentData): Promise<any> {
    if (this.useMockData) {
      return new Promise(resolve => {
        setTimeout(() => {
          const newAssignment = {
            id: Date.now().toString(),
            ...assignmentData,
            status: 'draft',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            submissions: 0,
            averageScore: 0,
          };
          mockAssessmentData.assignments.push(newAssignment);
          resolve(newAssignment);
        }, 1000);
      });
    }
    // Real API call would go here
    return Promise.reject(new Error('Not implemented'));
  }

  async updateAssignment(id: string, assignmentData: AssignmentData): Promise<any> {
    if (this.useMockData) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          const index = mockAssessmentData.assignments.findIndex(a => a.id === id);
          if (index !== -1) {
            mockAssessmentData.assignments[index] = {
              ...mockAssessmentData.assignments[index],
              ...assignmentData,
              updatedAt: new Date().toISOString(),
            };
            resolve(mockAssessmentData.assignments[index]);
          } else {
            reject(new Error('Assignment not found'));
          }
        }, 1000);
      });
    }
    // Real API call would go here
    return Promise.reject(new Error('Not implemented'));
  }

  async deleteAssignment(id: string): Promise<{ message: string }> {
    if (this.useMockData) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          const index = mockAssessmentData.assignments.findIndex(a => a.id === id);
          if (index !== -1) {
            mockAssessmentData.assignments.splice(index, 1);
            resolve({ message: 'Assignment deleted successfully' });
          } else {
            reject(new Error('Assignment not found'));
          }
        }, 500);
      });
    }
    // Real API call would go here
    return Promise.reject(new Error('Not implemented'));
  }

  // Question Bank Management
  async getQuestionBank(filters: Filters = {}): Promise<any> {
    if (this.useMockData) {
      return new Promise(resolve => {
        setTimeout(() => {
          let questions = [...mockAssessmentData.questionBank];

          if (filters.category) {
            questions = questions.filter(q => q.category === filters.category);
          }
          if (filters.type) {
            questions = questions.filter(q => q.type === filters.type);
          }
          if (filters.difficulty) {
            questions = questions.filter(q => q.difficulty === filters.difficulty);
          }
          if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            questions = questions.filter(
              q =>
                q.question.toLowerCase().includes(searchLower) ||
                q.tags.some((tag: string) => tag.toLowerCase().includes(searchLower))
            );
          }

          resolve({
            data: questions,
            total: questions.length,
            page: filters.page || 1,
            pageSize: filters.pageSize || 10,
          });
        }, 500);
      });
    }
    // Real API call would go here
    return Promise.resolve({ data: [], total: 0, page: 1, pageSize: 10 });
  }

  async createQuestion(questionData: QuestionData): Promise<any> {
    if (this.useMockData) {
      return new Promise(resolve => {
        setTimeout(() => {
          const newQuestion = {
            id: Date.now().toString(),
            ...questionData,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            usageCount: 0,
          };
          mockAssessmentData.questionBank.push(newQuestion);
          resolve(newQuestion);
        }, 1000);
      });
    }
    // Real API call would go here
    return Promise.reject(new Error('Not implemented'));
  }

  async updateQuestion(id: string, questionData: QuestionData): Promise<any> {
    if (this.useMockData) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          const index = mockAssessmentData.questionBank.findIndex(q => q.id === id);
          if (index !== -1) {
            mockAssessmentData.questionBank[index] = {
              ...mockAssessmentData.questionBank[index],
              ...questionData,
              updatedAt: new Date().toISOString(),
            };
            resolve(mockAssessmentData.questionBank[index]);
          } else {
            reject(new Error('Question not found'));
          }
        }, 1000);
      });
    }
    // Real API call would go here
    return Promise.reject(new Error('Not implemented'));
  }

  async deleteQuestion(id: string): Promise<{ message: string }> {
    if (this.useMockData) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          const index = mockAssessmentData.questionBank.findIndex(q => q.id === id);
          if (index !== -1) {
            mockAssessmentData.questionBank.splice(index, 1);
            resolve({ message: 'Question deleted successfully' });
          } else {
            reject(new Error('Question not found'));
          }
        }, 500);
      });
    }
    // Real API call would go here
    return Promise.reject(new Error('Not implemented'));
  }

  // Quiz Analytics
  async getQuizAnalytics(quizId: string): Promise<any> {
    if (this.useMockData) {
      return new Promise(resolve => {
        setTimeout(() => {
          const analytics = mockAssessmentData.quizAnalytics.find(a => a.quizId === quizId) || {
            quizId,
            totalAttempts: 0,
            averageScore: 0,
            passRate: 0,
            averageTimeSpent: 0,
            questionAnalytics: [],
          };
          resolve(analytics);
        }, 500);
      });
    }
    // Real API call would go here
    return Promise.reject(new Error('Not implemented'));
  }

  // Get categories for dropdowns
  async getCategories(): Promise<any> {
    if (this.useMockData) {
      return new Promise(resolve => {
        setTimeout(() => {
          resolve(mockAssessmentData.categories);
        }, 300);
      });
    }
    // Real API call would go here
    return Promise.reject(new Error('Not implemented'));
  }
}

export const assessmentService = new AssessmentService();